import { createSlice, PayloadAction, createAction } from '@reduxjs/toolkit';
import { AIConversationSession, ConversationMessage, SubmitMessageRequest } from '@/services/conversation';

// Async actions for saga
export const fetchSessionMessages = createAction<{ sessionId: string }>('conversationMessage/fetchSessionMessages');
export const fetchSessionMessagesSuccess = createAction<ConversationMessage[]>(
    'conversationMessage/fetchSessionMessagesSuccess',
);
export const fetchSessionMessagesFailure = createAction<string>('conversationMessage/fetchSessionMessagesFailure');

export const sendMessage = createAction<SubmitMessageRequest>('conversationMessage/sendMessage');
export const sendMessageSuccess = createAction<{ user_message: ConversationMessage; ai_response: ConversationMessage }>(
    'conversationMessage/sendMessageSuccess',
);
export const sendMessageFailure = createAction<string>('conversationMessage/sendMessageFailure');

export const pinMessage = createAction<{ messageId: string; isPinned?: boolean }>('conversationMessage/pinMessage');
export const pinMessageSuccess = createAction<ConversationMessage>('conversationMessage/pinMessageSuccess');
export const pinMessageFailure = createAction<string>('conversationMessage/pinMessageFailure');

export const fetchPinnedMessages = createAction<{ sessionId: string }>('conversationMessage/fetchPinnedMessages');
export const fetchPinnedMessagesSuccess = createAction<ConversationMessage[]>(
    'conversationMessage/fetchPinnedMessagesSuccess',
);
export const fetchPinnedMessagesFailure = createAction<string>('conversationMessage/fetchPinnedMessagesFailure');

export interface ConversationMessageState {
    sessions: AIConversationSession[];
    currentSession: AIConversationSession | null;
    sessionId: string;
    aiMessage: string;
    messages: ConversationMessage[];
    pinnedMessages: ConversationMessage[];
    currentSessionId: string | null;
    isLoading: boolean;
    isSending: boolean;
    isPinning: boolean;
    error: string | null;
    lastUpdated: string | null;
    suggestedQuestions: string[] | null;

    // Pagination
    totalCount: number;
    hasNext: boolean;
    hasPrevious: boolean;
}

const initialState: ConversationMessageState = {
    sessions: [],
    currentSession: null,
    sessionId: '',
    aiMessage: '',
    messages: [],
    pinnedMessages: [],
    currentSessionId: null,
    isLoading: false,
    isSending: false,
    isPinning: false,
    error: null,
    lastUpdated: null,
    suggestedQuestions: null,

    // Pagination
    totalCount: 0,
    hasNext: false,
    hasPrevious: false,
};

const conversationMessageSlice = createSlice({
    name: 'conversationMessage',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        resetState: () => initialState,
        setCurrentSessionId: (state, action: PayloadAction<string>) => {
            state.currentSessionId = action.payload;
            // Clear messages when switching sessions
            state.messages = [];
            state.pinnedMessages = [];
        },
        clearMessages: (state) => {
            state.messages = [];
            state.pinnedMessages = [];
        },
        addMessage: (state, action: PayloadAction<ConversationMessage>) => {
            // Check if message already exists
            const existingIndex = state.messages.findIndex((msg) => msg.id === action.payload.id);
            if (existingIndex >= 0) {
                state.messages[existingIndex] = action.payload;
            } else {
                // Insert message in correct sequence order
                const insertIndex = state.messages.findIndex(
                    (msg) => msg.sequence_number > action.payload.sequence_number,
                );
                if (insertIndex >= 0) {
                    state.messages.splice(insertIndex, 0, action.payload);
                } else {
                    state.messages.push(action.payload);
                }
            }
        },
        updateMessage: (state, action: PayloadAction<ConversationMessage>) => {
            const messageIndex = state.messages.findIndex((msg) => msg.id === action.payload.id);
            if (messageIndex >= 0) {
                state.messages[messageIndex] = action.payload;
            }

            // Update in pinned messages if it exists there
            const pinnedIndex = state.pinnedMessages.findIndex((msg) => msg.id === action.payload.id);
            if (pinnedIndex >= 0) {
                state.pinnedMessages[pinnedIndex] = action.payload;
            }
        },
        removeMessage: (state, action: PayloadAction<string>) => {
            state.messages = state.messages.filter((msg) => msg.id !== action.payload);
            state.pinnedMessages = state.pinnedMessages.filter((msg) => msg.id !== action.payload);
        },
        toggleMessagePin: (state, action: PayloadAction<ConversationMessage>) => {
            const message = action.payload;

            // Update in messages array
            const messageIndex = state.messages.findIndex((msg) => msg.id === message.id);
            if (messageIndex >= 0) {
                state.messages[messageIndex] = message;
            }

            // Add/remove from pinned messages
            if (message.is_pinned) {
                const pinnedIndex = state.pinnedMessages.findIndex((msg) => msg.id === message.id);
                if (pinnedIndex < 0) {
                    state.pinnedMessages.push(message);
                }
            } else {
                state.pinnedMessages = state.pinnedMessages.filter((msg) => msg.id !== message.id);
            }
        },
    },
    extraReducers: (builder) => {
        // Fetch Session Messages
        builder
            .addCase(fetchSessionMessages, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSessionMessagesSuccess, (state, action) => {
                state.isLoading = false;
                state.messages = action.payload.sort((a, b) => a.sequence_number - b.sequence_number);
                state.pinnedMessages = action.payload.filter((msg) => msg.is_pinned);
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(fetchSessionMessagesFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });

        // Send Message
        builder
            .addCase(sendMessage, (state) => {
                state.isSending = true;
                state.error = null;
            })
            .addCase(sendMessageSuccess, (state, action) => {
                state.isSending = false;

                // Add both user message and AI response
                const { user_message, ai_response } = action.payload;

                // Add user message
                const userInsertIndex = state.messages.findIndex(
                    (msg) => msg.sequence_number > user_message.sequence_number,
                );
                if (userInsertIndex >= 0) {
                    state.messages.splice(userInsertIndex, 0, user_message);
                } else {
                    state.messages.push(user_message);
                }

                // Add AI response
                const aiInsertIndex = state.messages.findIndex(
                    (msg) => msg.sequence_number > ai_response.sequence_number,
                );
                if (aiInsertIndex >= 0) {
                    state.messages.splice(aiInsertIndex, 0, ai_response);
                } else {
                    state.messages.push(ai_response);
                }

                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(sendMessageFailure, (state, action) => {
                state.isSending = false;
                state.error = action.payload;
            });

        // Pin Message
        builder
            .addCase(pinMessage, (state) => {
                state.isPinning = true;
                state.error = null;
            })
            .addCase(pinMessageSuccess, (state, action) => {
                state.isPinning = false;

                const message = action.payload;

                // Add/remove from pinned messages
                if (message.is_pinned) {
                    const pinnedIndex = state.pinnedMessages.findIndex((msg) => msg.id === message.id);
                    if (pinnedIndex < 0) {
                        state.pinnedMessages.push(message);
                    }
                } else {
                    state.pinnedMessages = state.pinnedMessages.filter((msg) => msg.id !== message.id);
                }

                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(pinMessageFailure, (state, action) => {
                state.isPinning = false;
                state.error = action.payload;
            });

        // Fetch Pinned Messages
        builder
            .addCase(fetchPinnedMessages, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchPinnedMessagesSuccess, (state, action) => {
                state.isLoading = false;
                state.pinnedMessages = action.payload.sort((a, b) => a.sequence_number - b.sequence_number);
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(fetchPinnedMessagesFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const {
    clearError,
    resetState,
    setCurrentSessionId,
    clearMessages,
    addMessage,
    updateMessage,
    removeMessage,
    toggleMessagePin,
} = conversationMessageSlice.actions;

export default conversationMessageSlice.reducer;
